import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import SEO from 'components/SEO';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const NotFound = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  // SEO for 404: noindex so search engines don't index error pages
  const seoData = {
    title: 'Page Not Found (404) | OctalChip',
    description: 'The page you requested could not be found. Return to OctalChip home for web development, mobile app development, and AI/ML services.',
    keywords: '404, page not found, OctalChip, web development, mobile app development',
    canonical: 'https://www.octalchip.com/',
    ogImage: 'https://www.octalchip.com/og-image.png',
    noIndex: true
  };

  useEffect(() => {
    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup on unmount
    return () => clearInterval(timer);
  }, [navigate]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleContactUs = () => {
    navigate('/#contact');
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        noIndex={seoData.noIndex}
      />
      <Header />
      
      {/* 404 Hero Section */}
      <section id="hero" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* 404 Number */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold text-[#00d690] opacity-20 mb-3 sm:mb-4">
                404
              </h1>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            </div>

            {/* Main Content */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-4">
              Oops! Page Not Found
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 mb-4 sm:mb-6 max-w-2xl mx-auto px-4">
              The page you're looking for seems to have wandered off into the digital wilderness. 
              Don't worry, we'll help you find your way back!
            </p>
            
            {/* Redirect Message */}
            <div className="bg-[#00d690]/10 border-2 border-[#00d690] rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              <div className="flex items-center justify-center gap-3 sm:gap-4">
                <div className="animate-spin">
                  <Icon name="RefreshCw" size={24} className="text-[#00d690]" />
                </div>
                <p className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">
                  Redirecting you to the homepage in <span className="text-[#00d690] font-bold text-xl sm:text-2xl">{countdown}</span> {countdown === 1 ? 'second' : 'seconds'}...
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-4">
              <Button
                variant="primary"
                animation="bounce"
                onClick={handleGoHome}
                className="bg-[#00d690] text-white px-6 sm:px-8 py-3 sm:py-4 font-semibold hover:bg-[#00b876] flex items-center justify-center gap-2 group text-sm sm:text-base"
                iconName="Home"
                iconPosition="left"
              >
                Back to Home
                <Icon name="ArrowRight" size={16} className="sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>

              <Button
                variant="outline"
                animation="pulse"
                onClick={handleGoBack}
                className="border-2 border-[#00d690] text-[#00d690] px-6 sm:px-8 py-3 sm:py-4 font-semibold hover:bg-[#00d690] hover:text-white flex items-center justify-center gap-2 text-sm sm:text-base"
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Go Back
              </Button>
            </div>

            {/* Help Section */}
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8 max-w-2xl mx-auto">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
                Need Help?
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                If you're looking for something specific, we're here to help! 
                Contact our team and we'll guide you to the right place.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center p-3 sm:p-4 rounded-lg bg-gray-50 hover:bg-[#00d690]/10 transition-all duration-300">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00d690] rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <Icon name="Search" size={18} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-1 sm:mb-2">Search Our Site</h4>
                  <p className="text-xs sm:text-sm text-gray-600">Use the navigation menu to find what you need</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-gray-50 hover:bg-[#00d690]/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-[#00d690] rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Mail" size={24} className="text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Contact Us</h4>
                  <p className="text-sm text-gray-600">Get in touch with our support team</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-gray-50 hover:bg-[#00d690]/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-[#00d690] rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Globe" size={24} className="text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Browse Services</h4>
                  <p className="text-sm text-gray-600">Explore our web development services</p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <Button
                  variant="primary"
                  animation="glow"
                  onClick={handleContactUs}
                  className="bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-6 py-3 font-semibold hover:shadow-lg flex items-center justify-center gap-2 mx-auto"
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  Get in Touch
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section id="quick-links" className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-6 sm:mb-8 px-4">
              Popular Pages
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Button
                variant="ghost"
                animation="scale"
                onClick={() => navigate('/')}
                className="p-6 bg-gray-50 hover:bg-[#00d690]/10 text-left group w-full h-full flex flex-col items-start"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Home" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Home</h4>
                <p className="text-sm text-gray-600">Back to our main page</p>
              </Button>

              <Button
                variant="ghost"
                animation="slide"
                onClick={() => navigate('/services')}
                className="p-6 bg-gray-50 hover:bg-[#00d690]/10 text-left group w-full h-full flex flex-col items-start"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Settings" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Services</h4>
                <p className="text-sm text-gray-600">View our development services</p>
              </Button>

              <Button
                variant="ghost"
                animation="shake"
                onClick={() => navigate('/#about')}
                className="p-6 bg-gray-50 hover:bg-[#00d690]/10 text-left group w-full h-full flex flex-col items-start"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Users" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">About Us</h4>
                <p className="text-sm text-gray-600">Learn more about OctalChip</p>
              </Button>

              <Button
                variant="ghost"
                animation="float"
                onClick={() => navigate('/#contact')}
                className="p-6 bg-gray-50 hover:bg-[#00d690]/10 text-left group w-full h-full flex flex-col items-start"
              >
                <div className="w-10 h-10 bg-[#00d690] rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Icon name="Mail" size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Contact</h4>
                <p className="text-sm text-gray-600">Get in touch with us</p>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NotFound;
